import { useEffect, useRef, useState } from "react";
import { FiLoader, FiX } from "react-icons/fi";
import { apiFetch } from "../utils/apiFetch";
import { lessonAccess } from "../utils/communityEntitlements.mjs";
import PremiumSetupPopup from "./PremiumSetupPopup";

export default function ResourceAccessEditor({ course, target, onClose, onSaved }) {
  const [access, setAccess] = useState(target.lesson ? lessonAccess(target.lesson) : "free");
  const [setup, setSetup] = useState(false), [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current, focused = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); focused?.focus(); };
  }, []);
  const choose = value => {
    if (value === "premium" && course.pricing?.access !== "paid" && !ready) { setSetup(true); return; }
    setAccess(value);
  };
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    if (access === "premium" && course.pricing?.access !== "paid" && !ready) { setSetup(true); return; }
    setBusy(true); setError("");
    try {
      const url = target.lesson ? `/classes/${target.lesson._id}/access` : `/groups/${course._id}/resource-access`;
      const body = target.lesson ? { access } : { access, scope: target.scope, moduleId: target.moduleId, all: target.all, itemIds: target.itemIds };
      const response = await apiFetch(`${process.env.API_URL}/api${url}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok) throw Error(data.message || "Could not update resource access");
      await onSaved(); onClose();
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  return <dialog ref={ref} aria-label="Edit resource access" onCancel={event => { event.preventDefault(); event.stopPropagation(); if (!busy) onClose(); }} className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-auto rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-6 shadow-xl backdrop:bg-black/60">
    <div className="mb-5 flex items-center justify-between gap-3"><h2 className="text-lg font-semibold">Edit resource access</h2><button type="button" disabled={busy} aria-label="Close access editor" title="Close" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-100"><FiX /></button></div>
    <p className="mb-4 break-words text-sm text-gray-600">{target.lesson?.title || `${target.itemIds.length} selected items`}</p>
    <form onSubmit={save} className="space-y-5">
      <label className="block text-sm font-semibold">Resource access<select aria-label="Resource access" value={access} disabled={busy} onChange={event => choose(event.target.value)} className="mt-2 h-11 w-full rounded-md border px-3"><option value="free">All members</option><option value="premium">Premium members only</option></select></label>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <button disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded-md bg-orange-700 px-4 font-semibold text-white disabled:opacity-50">{busy && <FiLoader className="animate-spin" />}Save access</button>
    </form>
    {setup && <PremiumSetupPopup onClose={() => setSetup(false)} onComplete={() => { setReady(true); setAccess("premium"); }} />}
  </dialog>;
}
